import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMixpanel } from '../../context/MixpanelProvider';
import { useVerifyLink } from '../../hooks/getQueries';
import { AUTH_EVENTS } from '../../utils/eventSchema';
import VerifyEmail from './VerifyEmail';

function VerifyLink() {
  const hasHandledVerification = useRef(false);

  const [isValid, setIsValid] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailToVerify, setEmailToVerify] = useState(null);
  const params = new URLSearchParams(window.location.search);

  const token = params.get('token');
  const { data: verifyLinkData, isError } = useVerifyLink({ token });
  const { trackEvent, identifyUser, aliasUser } = useMixpanel();

  useEffect(() => {
    if (hasHandledVerification.current) return;
    if (verifyLinkData) {
      hasHandledVerification.current = true;

      const { message, user_id, user_email } = verifyLinkData;

      if (user_id) {
        // Step 1: Get the anonymous ID that was being tracked
        const anonymousId = mixpanel.get_distinct_id();

        // Step 2: Link anonymous user to permanent user ID
        aliasUser(anonymousId, user_id);

        // Step 3: Identify the user with their permanent attributes
        identifyUser(user_id, {
          email: user_email,
          $email: user_email,
          verified: true,
          email_verified_at: new Date().toISOString(),
        });

        // Step 4: Track the email verification event with user_id
        trackEvent('Auth', {
          ...AUTH_EVENTS.EMAIL_VERIFIED,
          user_id,
          email: user_email,
          verification_method: 'link',
          verified_at: new Date().toISOString(),
        });
      } else {
        // Fallback if no user_id is available
        trackEvent('Auth', {
          ...AUTH_EVENTS.EMAIL_VERIFIED,
          email: user_email,
          verification_method: 'link',
          verified_at: new Date().toISOString(),
        });
      }

      toast.success(message, {
        toastId: 'email-verification-success',
        autoClose: 2000,
        onClose: () => {
          setIsValid(true);
          navigate('/signin');
        },
      });
    }

    if (isError) {
      hasHandledVerification.current = true;
      toast.error('The link has expired or is invalid.', {
        toastId: 'email-verification-error',
        autoClose: 3000,
      });
      setIsValid(false);
      setOpenDialog(true);
    }
  }, [verifyLinkData, isError, token]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Clear error when user types
    setEmailError('');
  };

  const handleResendVerificationEmail = async () => {
    // Validate email before submitting
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      // If successful, close the dialog and set the email to verify flag
      setOpenDialog(false);
      setEmailToVerify(true);
    } catch (error) {
      console.error('Error:', error);

      if (axios.isAxiosError(error)) {
        // Extracting the message from the error response
        const message = error.response.data.detail;
        // Showing the error in a toast notification
        setError(message);
      } else {
        // Handle any unexpected errors
        console.error('Unexpected Error:', error);
        toast.error('An unexpected error occurred.');
      }
    }
  };

  if (emailToVerify) {
    const params = new URLSearchParams(window.location.search);
    params.delete('token');
    window.history.replaceState({}, '', `${window.location.pathname}${params.toString()}`);
    return <VerifyEmail email={email} />;
  }
  if (isValid === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress />
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'text.secondary',
          }}
        >
          Verifying email<span>...</span>
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Dialog for resending verification email */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          Resend Verification Email
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenDialog(false);
              navigate('/signin');
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 2,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Enter your email address to resend the verification email
          </Typography>
          <FormLabel>Email *</FormLabel>
          <TextField
            margin="dense"
            type="email"
            fullWidth
            value={email}
            onChange={handleEmailChange}
            variant="outlined"
            required
            error={!!emailError}
            helperText={emailError}
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              navigate('/signin');
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handleResendVerificationEmail} color="primary">
            Resend
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default VerifyLink;
